import { FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_DESCRIPTIONS_PROPS } from 'configs/common';
import { MAX_AD_BID_BUDGET } from '../configs';
import { useCountry, useLocaleTimeUtils } from 'hooks';
import { FORM_VALIDATORS } from 'utils/FormValidators';
import { disableWheelChange } from 'utils/InputComponent';
import { DATE_TIME_INTL_FORMAT } from 'utils/date';
import { handleFormErrorResponse } from 'utils/handleFormErrorResponse';
import { IAdSection } from 'types/adManagement';
import { IGenericEntity } from 'types/common';
import FormSectionLabels from 'components/FormSectionLabels';
import PageLayout from 'components/PageLayout';
import { SchemaComponent } from 'components/SchemaComponent/SchemaComponent';
import SelectAdSection from 'components/_input-components/GenericListSelectors/SelectAdSection';
import { useAdBid } from '../useAdBid';
import { BidInput } from './BidInput';
import { BudgetProgressBar } from './BudgetProgressBar';
import { HowItWorksTooltip } from './HowItWorksTooltip';
import dayjs, { Dayjs } from 'dayjs';
import { Button, Col, DatePicker, Descriptions, Flex, Form, Input, InputNumber, Row, Typography as T } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { PageHeader } from '@ant-design/pro-layout';

export interface IEditCreateBidForm {
	id?: number;
	ad_section_id: IAdSection;
	start_date: Dayjs;
	budget: number;
	increase_budget: number;
	price_per_click: number;
}

export const AdBidCreateEdit: FC = () => {
	const { t: tCommon } = useTranslation('common');
	const { t: tAdManagement } = useTranslation('ad-management');
	const { datePickerDisableDatesBeforeToday, disablePastTime, changeTimezoneToSelectedTimeZone } =
		useLocaleTimeUtils();

	const { adBidId, vendorId, data: adBid, create, edit } = useAdBid();

	const isEdit = adBidId !== undefined;

	const navigate = useNavigate();

	const { selectedCountryCurrencyCode } = useCountry();

	const [form] = useForm<IEditCreateBidForm>();

	const ad_section_id = useWatch('ad_section_id', form);
	const budget_increase = useWatch<number>('increase_budget', form);
	const budget_total = adBid?.budget?.total ?? 0;
	const budget_spent = adBid?.budget?.spent ?? 0;
	const budget_new = budget_total + (budget_increase ?? 0);

	// ! handlers
	const handleSubmit = ({
		ad_section_id,
		budget = adBid?.budget.total ?? 0,
		increase_budget = 0,
		...values
	}: IEditCreateBidForm) => {
		const payload = {
			...values,
			ad_section_id: ad_section_id?.id,
			start_date: changeTimezoneToSelectedTimeZone(values.start_date).toISOStringWithZone(),
			budget: +budget + +increase_budget,
		};

		let promise;
		if (adBidId) {
			promise = edit(+adBidId, payload);
		} else {
			promise = create(payload);
		}

		promise?.then(() => navigate('..')).catch((e) => handleFormErrorResponse(e, form));
	};

	// ! effects
	useEffect(() => {
		if (!adBidId || !adBid) return;

		form.setFieldsValue({
			...adBid,
			budget: adBid.budget.total,
			start_date: dayjs(adBid.start_date),
			ad_section_id: adBid.ad_section,
		});
	}, [adBid, adBidId, form]);

	// ! render
	return (
		<PageLayout
			header={
				<PageHeader
					title={tAdManagement(isEdit ? 'page_titles.edit' : 'page_titles.create', { id: adBidId })}
					onBack={() => navigate('..')}
				/>
			}
			withInnerPadding
		>
			<Form
				form={form}
				layout='vertical'
				onFinish={handleSubmit}
			>
				<Form.Item
					name='id'
					hidden
				>
					<Input />
				</Form.Item>

				<Row gutter={[16, 16]}>
					<>
						{/* Select Ad Section */}
						<Col xs={24}>
							<FormSectionLabels
								label={tAdManagement('form.ad_section.title')}
								extra={tAdManagement('form.ad_section.description')}
							/>
						</Col>

						<Col xs={24}>
							<Form.Item
								name='ad_section_id'
								label={tAdManagement('form.ad_section.label')}
								rules={[FORM_VALIDATORS.REQUIRED()]}
								getValueFromEvent={(_, option) => option?.object}
								getValueProps={(option) => ({ value: option?.id })}
							>
								<SelectAdSection
									vendorId={vendorId}
									disabled={isEdit}
									defaultOption={
										ad_section_id ? (ad_section_id as IGenericEntity<IAdSection>) : undefined
									}
								/>
							</Form.Item>

							{ad_section_id && (
								<>
									<T.Paragraph>{tCommon('action_buttons.preview')}</T.Paragraph>
									<Flex>
										<SchemaComponent type={(ad_section_id as any).app_component.type} />
									</Flex>
								</>
							)}
						</Col>
					</>
					<>
						{/* Duration */}
						<Col xs={24}>
							<FormSectionLabels
								label={tAdManagement('form.duration.title')}
								extra={tAdManagement('form.duration.description')}
							/>
						</Col>

						<Col xs={24}>
							<Form.Item
								name='start_date'
								label={tAdManagement('form.start_date.label')}
								rules={[FORM_VALIDATORS.REQUIRED()]}
							>
								<DatePicker
									placeholder={tAdManagement('form.start_date.placeholder')}
									disabledDate={datePickerDisableDatesBeforeToday}
									disabledTime={disablePastTime}
									showTime
									format={DATE_TIME_INTL_FORMAT}
									className='w-100'
								/>
							</Form.Item>
						</Col>
					</>

					{!isEdit && (
						<>
							{/* Budget */}
							<Col xs={24}>
								<FormSectionLabels
									label={tAdManagement('form.budget.title')}
									extra={tAdManagement('form.budget.description')}
								/>
							</Col>

							<Col flex={1}>
								<Form.Item
									name='budget'
									label={tAdManagement('form.budget.label')}
									extra={tAdManagement('form.budget.extra')}
									tooltip={tAdManagement('form.budget.tooltip', {
										maxBudget: MAX_AD_BID_BUDGET,
										currency: selectedCountryCurrencyCode,
									})}
									rules={[FORM_VALIDATORS.REQUIRED()]}
								>
									<Input
										placeholder={tAdManagement('form.budget.placeholder', {
											currency: selectedCountryCurrencyCode,
										})}
										min={0}
										type='number'
										suffix={selectedCountryCurrencyCode}
										onWheel={disableWheelChange}
									/>
								</Form.Item>
							</Col>
						</>
					)}

					{isEdit && (
						<>
							{/* Increase Budget */}
							<Col xs={24}>
								<FormSectionLabels
									label={tAdManagement('form.increase_budget.title')}
									extra={tAdManagement('form.increase_budget.description')}
								/>
							</Col>

							<Col span={24}>
								<Form.Item
									name='increase_budget'
									label={tAdManagement('form.increase_budget.label')}
									extra={tAdManagement('form.increase_budget.extra')}
									tooltip={tAdManagement('form.increase_budget.tooltip', {
										maxBudget: MAX_AD_BID_BUDGET,
										currency: selectedCountryCurrencyCode,
									})}
								>
									<InputNumber
										placeholder={tAdManagement('form.increase_budget.placeholder')}
										min={0}
										max={MAX_AD_BID_BUDGET - budget_total}
										step={1000}
										type='number'
										className='w-100'
										suffix={selectedCountryCurrencyCode}
										onWheel={disableWheelChange}
									/>
								</Form.Item>

								<Form.Item>
									<Descriptions
										{...DEFAULT_DESCRIPTIONS_PROPS}
										bordered
										styles={{
											label: { width: '50%', minWidth: '180px' },
											content: { maxWidth: 1 }, // Note: workaround to Antd styles to respect ellipses on the final link (CopyToClipBoard component)
										}}
									>
										<Descriptions.Item label={tAdManagement('form.increase_budget.spent_label')}>
											<BudgetProgressBar
												value={budget_spent}
												type='spent'
											/>
										</Descriptions.Item>

										<Descriptions.Item
											label={tAdManagement('form.increase_budget.current_budget_label')}
										>
											<BudgetProgressBar
												value={budget_total}
												type='current_budget'
											/>
										</Descriptions.Item>
										{budget_increase > 0 && (
											<Descriptions.Item
												label={tAdManagement('form.increase_budget.new_budget_label')}
											>
												<BudgetProgressBar
													value={budget_new}
													type='new_budget'
												/>
											</Descriptions.Item>
										)}
									</Descriptions>
								</Form.Item>
							</Col>
						</>
					)}

					<>
						{/* BID */}
						<Col xs={24}>
							<FormSectionLabels
								label={tAdManagement('form.bid.title')}
								extra={
									<Trans
										t={tAdManagement}
										i18nKey='form.bid.description'
										components={[<HowItWorksTooltip />]}
									/>
								}
							/>
						</Col>
						<Col xs={24}>
							<Form.Item>
								<BidInput adSectionId={ad_section_id?.id} />
							</Form.Item>
						</Col>
					</>
				</Row>
				{/* Actions Buttons */}
				<Flex
					justify='center'
					gap='middle'
				>
					<Button
						type='primary'
						htmlType='submit'
						className='min-w-save-btn'
					>
						{tCommon('action_buttons.save')}
					</Button>
				</Flex>
			</Form>
		</PageLayout>
	);
};
